import { motion } from "motion/react";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";

import { installSimNoScrollGuard } from "./simNoScroll";

/**
 * Simulated pointer overlay for the sim-mode widgets.
 *
 * Every sim (`YaasEnableRewardsSim`, `BuyWidget`, `TradeWidget`, …)
 * auto-plays a scripted timeline of interactions (typing, toggling,
 * submitting). This primitive renders a fake OS-style cursor that travels
 * to each interaction target in lockstep with that timeline, so the viewer
 * sees *where* each scripted action happens. It's a purely visual layer —
 * the interaction STATES (hover/press/typing) are still driven by the
 * widgets' existing `forceHover` / `forcePressed` / typing choreography;
 * the cursor just points at them.
 *
 * ## Usage
 * A driver calls {@link useSimCursor} once, attaches `containerRef` to the
 * positioned wrapper around its widget, and renders `layer` inside it:
 *
 * ```tsx
 * const cursor = useSimCursor();
 * return (
 *   <div ref={cursor.containerRef} className="relative">
 *     {widgetContent}
 *     {cursor.layer}
 *   </div>
 * );
 * ```
 *
 * Then, on the same `setTimeout` offsets the timeline already uses, it
 * schedules moves just BEFORE each interaction beat so the cursor arrives
 * as the state plays:
 *
 * ```tsx
 * cursor.moveTo("destination");           // travels (≤1s), morphs to text I-beam
 * // …existing typing beat fires…
 * cursor.moveTo("submit");                // travels, morphs to pointer hand
 * cursor.click();                         // press pulse; button's forcePressed plays
 * ```
 *
 * ## Targets
 * Interactive elements carry `data-sim-cursor-key="<key>"`. The cursor
 * glyph auto-detects from the element: `<input>`/`<textarea>` → text
 * I-beam, `<button>`/`<a>`/`[role=button]` → pointer hand, else the arrow.
 * Override with `data-sim-cursor="default|pointer|text"` on the element or
 * `{ cursor }` in the move opts.
 */

export type SimCursorType = "default" | "pointer" | "text";

/** Travel speed — duration is derived from distance so short hops feel
 *  snappy and long ones stay under the {@link MAX_MOVE_MS} cap. */
const CURSOR_SPEED_PX_PER_S = 1150;
const MIN_MOVE_MS = 220;
/** Hard cap per the brief: no single move takes longer than 1s. */
const MAX_MOVE_MS = 1000;
/** How long the click press-pulse (ring + glyph dip) lasts. */
const CLICK_PULSE_MS = 260;
/**
 * Travel easing for the cursor's x/y. A short-tail ease-out: the cursor
 * accelerates, then decelerates into the target WITHOUT a long creep. The old
 * curve ([0.33,0,0.2,1]) parked the cursor ~4px from the target for ~5 frames
 * before the forced-hover beat painted — a visible "arrived, then highlight"
 * lag. Pulling x2 to 0.8 collapses that tail to ~1 frame while keeping the
 * landing soft (no overshoot/hard stop). Measured: reaches 98% of distance at
 * t≈0.95 (vs t≈0.82 for the old curve), arrival velocity ≈3% of peak.
 */
const CURSOR_EASE: [number, number, number, number] = [0.4, 0, 0.8, 1];

export interface SimCursorMoveOptions {
    /** Force the glyph type instead of auto-detecting from the element. */
    cursor?: SimCursorType;
    /** Override the distance-derived duration (still clamped to ≤1s). */
    durationMs?: number;
    /** Nudge from the resolved point, in container px. */
    offset?: { x?: number; y?: number };
    /** Where on the element to point. `center` (default) or `left` (a bit
     *  inset from the left edge — natural for a wide input/text field). */
    align?: "center" | "left";
}

export interface SimCursorController {
    /** Attach to the positioned (`relative`) wrapper around the widget. */
    containerRef: React.RefObject<HTMLDivElement | null>;
    /** Render this node inside the container (it overlays, no pointer
     *  events, high z). */
    layer: React.ReactNode;
    /** Travel to the element carrying `data-sim-cursor-key="<key>"`. No-op
     *  if the key isn't found (target not mounted yet). */
    moveTo: (key: string, opts?: SimCursorMoveOptions) => void;
    /** Travel to a specific element (escape hatch for keyless targets). */
    moveToElement: (
        el: HTMLElement | null,
        opts?: SimCursorMoveOptions
    ) => void;
    /** Play a press pulse at the current position. Pass `{ key }` to move
     *  there first (a short hop) before pulsing. */
    click: (opts?: { key?: string }) => void;
    /** Fade the cursor out (e.g. between loop cycles). */
    hide: () => void;
    /** Fade the cursor back in at its current position. */
    show: () => void;
}

interface Point {
    x: number;
    y: number;
}

function detectType(el: HTMLElement): SimCursorType {
    const explicit = el.getAttribute("data-sim-cursor");
    if (
        explicit === "pointer" ||
        explicit === "text" ||
        explicit === "default"
    ) {
        return explicit;
    }
    if (el.matches("input, textarea, [contenteditable='true']")) return "text";
    if (
        el.matches("button, a, [role='button'], label, select") ||
        el.closest("button, a, [role='button']")
    ) {
        return "pointer";
    }
    return "default";
}

/** Per-type hotspot: where the glyph's meaningful point sits relative to
 *  its own top-left, so we can offset the box to land that point on the
 *  target coordinate. */
const HOTSPOT: Record<SimCursorType, Point> = {
    default: { x: 2, y: 2 }, // arrow tip
    pointer: { x: 9, y: 3 }, // fingertip
    text: { x: 6, y: 11 } // I-beam centre
};

function CursorGlyph({ type }: { type: SimCursorType }): React.ReactElement {
    if (type === "pointer") {
        // Pointing hand.
        return (
            <svg width="26" height="30" viewBox="0 0 26 30" fill="none">
                <path
                    d="M9 3.2c0-1.2.9-2.2 2-2.2s2 1 2 2.2V13l1.4-3.1c.4-.9 1.4-1.3 2.3-.9.7.3 1.1 1 1.1 1.7l1-.6c.8-.5 1.9-.2 2.4.7.2.4.3.8.2 1.2.8-.2 1.6.2 1.9 1 .5 1.2.5 3-.2 5.4l-.9 3.1c-.7 2.4-2.9 4.1-5.4 4.1h-3.6c-1.6 0-3.1-.7-4.1-2L5 20c-.7-1-.5-2.3.4-3 .8-.6 1.9-.6 2.7.1L9 17.8V3.2Z"
                    fill="#F3F3E8"
                    stroke="#1B1C20"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }
    if (type === "text") {
        // I-beam.
        return (
            <svg width="12" height="24" viewBox="0 0 12 24" fill="none">
                <path
                    d="M3 2h6M3 22h6M6 2v20"
                    stroke="#1B1C20"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                />
                <path
                    d="M3 2h6M3 22h6M6 2v20"
                    stroke="#F3F3E8"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                />
            </svg>
        );
    }
    // Default arrow.
    return (
        <svg width="22" height="24" viewBox="0 0 22 24" fill="none">
            <path
                d="M2 2 L2 18.7 L6.4 14.6 L9.3 21.2 L12 20 L9.1 13.5 L15.2 13.1 Z"
                fill="#F3F3E8"
                stroke="#1B1C20"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export function useSimCursor(): SimCursorController {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Every cursor-driven sim attaches `containerRef` to its positioned
    // wrapper AND portals any Base UI popup (the policy-type Select list) INTO
    // that same element. Mark it `data-sim-no-scroll` and install a one-time
    // guard so opening/closing that popup inside the fixed `overflow-hidden`
    // SimFrame can't yank the whole page toward the sim. The guard kills CSS
    // scroll anchoring inside the sim subtree (the measured yank vector) and
    // no-ops `scrollIntoView` for sim descendants (Base UI's open-scroll of the
    // selected option). Sims are passive, auto-playing displays — neither is
    // ever wanted inside one, so suppressing both is always correct. See
    // `simNoScroll`.
    useEffect(() => {
        installSimNoScrollGuard();
        const el = containerRef.current;
        if (!el) return;
        el.setAttribute("data-sim-no-scroll", "");
    }, []);

    const posRef = useRef<Point>({ x: 0, y: 0 });
    const pendingTypeRef = useRef<SimCursorType>("default");
    const clickTimerRef = useRef<number | null>(null);

    const [pos, setPos] = useState<Point>({ x: 0, y: 0 });
    const [duration, setDuration] = useState(MAX_MOVE_MS / 1000);
    const [type, setType] = useState<SimCursorType>("default");
    const [visible, setVisible] = useState(false);
    const [pressing, setPressing] = useState(false);
    const [seeded, setSeeded] = useState(false);

    // Seed a rest position (bottom-centre of the container) once it's laid
    // out, so the first move animates in from a natural spot rather than
    // flying out of the top-left corner. Runs after every commit on purpose:
    // the consumer attaches `containerRef` and lays it out on its own
    // schedule, so this retries until it can measure, then `seeded` stops it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- retries every commit until the container can be measured; the `seeded` guard ends it, so no update chain.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el || seeded) return;
        const r = el.getBoundingClientRect();
        if (r.width === 0) return;
        const rest = { x: r.width * 0.5, y: r.height * 0.92 };
        posRef.current = rest;
        setPos(rest);
        setSeeded(true);
    });

    const moveToElement = useCallback(
        (el: HTMLElement | null, opts?: SimCursorMoveOptions): void => {
            const container = containerRef.current;
            if (!container || !el) return;
            const cRect = container.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            const align = opts?.align ?? "center";
            const pointX =
                align === "left"
                    ? eRect.left + Math.min(16, eRect.width / 2)
                    : eRect.left + eRect.width / 2;
            const pointY = eRect.top + eRect.height / 2;
            const nextType = opts?.cursor ?? detectType(el);
            const x = pointX - cRect.left + (opts?.offset?.x ?? 0);
            const y = pointY - cRect.top + (opts?.offset?.y ?? 0);

            const prev = posRef.current;
            const dist = Math.hypot(x - prev.x, y - prev.y);
            const dur =
                opts?.durationMs !== undefined
                    ? // Explicit override — honour it, but keep the ≤1s cap.
                      Math.min(MAX_MOVE_MS, Math.max(0, opts.durationMs))
                    : Math.min(
                          MAX_MOVE_MS,
                          Math.max(
                              MIN_MOVE_MS,
                              (dist / CURSOR_SPEED_PX_PER_S) * 1000
                          )
                      );
            pendingTypeRef.current = nextType;
            posRef.current = { x, y };
            setDuration(dur / 1000);
            setType("default"); // arrow while travelling; morph on arrival
            setPos({ x, y });
            setVisible(true);
        },
        []
    );

    const moveTo = useCallback(
        (key: string, opts?: SimCursorMoveOptions): void => {
            const container = containerRef.current;
            if (!container) return;
            const el = container.querySelector<HTMLElement>(
                `[data-sim-cursor-key="${CSS.escape(key)}"]`
            );
            moveToElement(el, opts);
        },
        [moveToElement]
    );

    const click = useCallback(
        (opts?: { key?: string }): void => {
            if (opts?.key) moveTo(opts.key);
            setPressing(true);
            if (clickTimerRef.current)
                window.clearTimeout(clickTimerRef.current);
            clickTimerRef.current = window.setTimeout(
                () => setPressing(false),
                CLICK_PULSE_MS
            );
        },
        [moveTo]
    );

    const hide = useCallback((): void => setVisible(false), []);
    const show = useCallback((): void => setVisible(true), []);

    const hotspot = HOTSPOT[type];

    const layer = (
        <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[999] overflow-visible"
        >
            <motion.div
                initial={false}
                animate={{ x: pos.x, y: pos.y, opacity: visible ? 1 : 0 }}
                transition={{
                    // Tight ease-out: control point x2 pulled to 0.8 so the
                    // deceleration tail is short — the cursor arrives crisply
                    // instead of creeping the last ~15-20% of the distance.
                    // The forced-hover beat paints at the animation's end, so a
                    // long tail (the old [0.33,0,0.2,1]) made the cursor look
                    // arrived for ~5 frames before the highlight landed. This
                    // curve keeps a natural soft landing (arrival velocity still
                    // ~3% of peak, no abrupt stop or overshoot) while cutting the
                    // perceived arrival→highlight gap to ~1 frame. See CURSOR_EASE.
                    x: { duration, ease: CURSOR_EASE },
                    y: { duration, ease: CURSOR_EASE },
                    opacity: { duration: 0.22 }
                }}
                onAnimationComplete={() => setType(pendingTypeRef.current)}
                style={{ position: "absolute", top: 0, left: 0 }}
            >
                <motion.div
                    animate={{ scale: pressing ? 0.8 : 1 }}
                    transition={{ duration: 0.12, ease: "easeOut" }}
                    style={{
                        position: "absolute",
                        top: -hotspot.y,
                        left: -hotspot.x,
                        filter: "drop-shadow(0 1px 2px rgba(27,28,32,0.35))"
                    }}
                >
                    <CursorGlyph type={type} />
                </motion.div>
                {pressing && (
                    <motion.span
                        initial={{ scale: 0.3, opacity: 0.55 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{
                            duration: CLICK_PULSE_MS / 1000,
                            ease: "easeOut"
                        }}
                        style={{
                            position: "absolute",
                            top: -14,
                            left: -14,
                            width: 28,
                            height: 28,
                            borderRadius: "9999px",
                            border: "2px solid rgba(253,64,23,0.9)" // ember
                        }}
                    />
                )}
            </motion.div>
        </div>
    );

    return {
        containerRef,
        layer,
        moveTo,
        moveToElement,
        click,
        hide,
        show
    };
}
